/**
 * How It Works — Complete user journey video
 * 5100 frames at 30fps = 170 seconds ≈ 2:50
 *
 * Complete flow — all full-screen, no browser chrome, no zoom/pan/scale:
 *  0-180     Opening: brand + what is Raley
 *  180-420   Sign up / Login flow
 *  420-720   Create first competition + configure scoring
 *  720-1080  Invite judges (Setup page)
 *  1080-1440 Add & edit riders with photos
 *  1440-1800 Create divisions & heats (Setup page)
 *  1800-2340 Judge: multi-attempt scoring
 *  2340-2880 Producer: overlay controls, attempt management
 *  2880-3420 OBS broadcast output (themed overlay)
 *  3420-4050 Competition hub (CompetitionView)
 *  4050-4680 Features highlight
 *  4680-4860 Training & certificates
 *  4860-5100 CTA
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, Img, Easing } from "remotion";
import { SlideIn } from "../components/SlideIn";
import { FadeTransition } from "../components/FadeTransition";
import { DashboardScreen } from "./screens/DashboardScreen";
import { ProducerScreen } from "./screens/ProducerScreen";
import { JudgeScreen } from "./screens/JudgeScreen";
import { OverlayScreen } from "./screens/OverlayScreen";

const IMG = {
  wakeFlip: "https://images.unsplash.com/photo-1530870110042-98b2cb110834?w=1920&q=80",
  wakeJump: "https://images.unsplash.com/photo-1564415637254-92c66292cd64?w=1920&q=80",
  wakeRide: "https://images.unsplash.com/photo-1596463989166-9c893526c948?w=1920&q=80",
  wakeBoard: "https://images.unsplash.com/photo-1574482620811-1aa16ffe3c82?w=1920&q=80",
  lake: "https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=1920&q=80",
};

// ── Exact CSS variable values from styles.css ──
const C = {
  bg: "#0a0a0b", surface: "#111113", surface2: "#1a1a1e", surface3: "#222228",
  border: "#2a2a32", text: "#e4e4e7", textMuted: "#71717a", textDim: "#52525b",
  accent: "#3b82f6", danger: "#ef4444", success: "#22c55e", warning: "#eab308",
};
const F = { sans: "'Inter', sans-serif", mono: "'JetBrains Mono', monospace" };

export function HowItWorks() {
  return (
    <AbsoluteFill style={{ backgroundColor: C.bg, fontFamily: F.sans }}>
      <Sequence durationInFrames={180}><OpeningScene /></Sequence>
      <Sequence from={180} durationInFrames={240}><FadeTransition durationInFrames={240} fadeIn={12} fadeOut={12}><SignupScene /></FadeTransition></Sequence>
      <Sequence from={420} durationInFrames={300}><FadeTransition durationInFrames={300} fadeIn={12} fadeOut={12}><CreateCompetitionScene /></FadeTransition></Sequence>
      <Sequence from={720} durationInFrames={360}><FadeTransition durationInFrames={360} fadeIn={12} fadeOut={12}><InviteJudgesScene /></FadeTransition></Sequence>
      <Sequence from={1080} durationInFrames={360}><FadeTransition durationInFrames={360} fadeIn={12} fadeOut={12}><RidersScene /></FadeTransition></Sequence>
      <Sequence from={1440} durationInFrames={360}><FadeTransition durationInFrames={360} fadeIn={12} fadeOut={12}><SetupHeatsScene /></FadeTransition></Sequence>
      <Sequence from={1800} durationInFrames={540}><FadeTransition durationInFrames={540} fadeIn={12} fadeOut={12}><JudgeDashboardScene /></FadeTransition></Sequence>
      <Sequence from={2340} durationInFrames={540}><FadeTransition durationInFrames={540} fadeIn={12} fadeOut={12}><ProducerDashboardScene /></FadeTransition></Sequence>
      <Sequence from={2880} durationInFrames={540}><FadeTransition durationInFrames={540} fadeIn={12} fadeOut={12}><OBSBroadcastScene /></FadeTransition></Sequence>
      <Sequence from={3420} durationInFrames={630}><FadeTransition durationInFrames={630} fadeIn={12} fadeOut={12}><CompetitionHubScene /></FadeTransition></Sequence>
      <Sequence from={4050} durationInFrames={630}><FeaturesHighlight /></Sequence>
      <Sequence from={4680} durationInFrames={180}><FadeTransition durationInFrames={180} fadeIn={12} fadeOut={12}><TrainingScene /></FadeTransition></Sequence>
      <Sequence from={4860} durationInFrames={240}><CTAScene /></Sequence>
    </AbsoluteFill>
  );
}

// ── Shared overlay label component ──

function StepLabel({ step, color, title, bullets }: { step: string; color: string; title: string; bullets: string[] }) {
  return (
    <div style={{
      position: "absolute", bottom: 40, left: 40,
      background: "rgba(10,10,11,0.92)", border: "1px solid rgba(255,255,255,0.1)",
      backdropFilter: "blur(12px)", padding: "20px 28px", maxWidth: 420, zIndex: 10,
    }}>
      <div style={{ fontSize: 10, color, textTransform: "uppercase", letterSpacing: 2, fontFamily: F.mono, marginBottom: 6 }}>{step}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: 10 }}>{title}</div>
      {bullets.map((b) => (
        <div key={b} style={{ fontSize: 12, color: C.textDim, display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
          <span style={{ color: C.success, fontSize: 14 }}>✓</span> {b}
        </div>
      ))}
    </div>
  );
}

// ── Scenes ──

function OpeningScene() {
  const frame = useCurrentFrame();
  const images = [IMG.wakeFlip, IMG.wakeJump, IMG.wakeRide];
  const imgIndex = Math.min(Math.floor(frame / 45), images.length - 1);
  const brandOp = interpolate(frame, [60, 90], [0, 1], { extrapolateRight: "clamp" });
  const brandScale = interpolate(frame, [60, 90], [0.85, 1], { extrapolateRight: "clamp", easing: Easing.out(Easing.back(1.5)) });
  const textOp = interpolate(frame, [100, 140], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill>
      <Img src={images[imgIndex]} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.3) contrast(1.2)" }} />
      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: brandOp, transform: `scale(${brandScale})` }}>
        <div style={{ fontSize: 110, fontWeight: 800, letterSpacing: 14, color: "#fff", textShadow: "0 0 60px rgba(59,130,246,0.4)" }}>RALEY</div>
      </AbsoluteFill>
      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", paddingTop: 180, opacity: textOp }}>
        <div style={{ fontSize: 48, fontWeight: 800, color: "#fff", textAlign: "center", letterSpacing: 2 }}>How It Works</div>
        <div style={{ fontSize: 18, color: "#94a3b8", marginTop: 16, textAlign: "center", maxWidth: 600 }}>From sign-up to live broadcast — here's the complete Raley workflow</div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
}

function SignupScene() {
  return (
    <AbsoluteFill>
      <LoginScreenMock />
      <StepLabel step="Step 1" color={C.accent} title="Sign Up Free" bullets={["Google or email sign-in", "No credit card needed", "Takes 30 seconds"]} />
    </AbsoluteFill>
  );
}

function CreateCompetitionScene() {
  return (
    <AbsoluteFill>
      <DashboardScreen />
      <StepLabel step="Step 2" color={C.success} title="Create a Competition" bullets={["5 scoring models available", "1-10 judges configurable", "Name and describe your event"]} />
    </AbsoluteFill>
  );
}

function InviteJudgesScene() {
  return (
    <AbsoluteFill>
      <SetupInviteScreenMock />
      <StepLabel step="Step 3" color={C.warning} title="Invite Your Team" bullets={["Judge, Producer, Admin roles", "Email invite — one click to join", "Named judges for credibility"]} />
    </AbsoluteFill>
  );
}

function RidersScene() {
  return (
    <AbsoluteFill>
      <RidersScreenMock />
      <StepLabel step="Step 4" color={C.danger} title="Add Riders" bullets={["Upload HD photos", "Custom colours & numbers", "Reusable across all events"]} />
    </AbsoluteFill>
  );
}

function SetupHeatsScene() {
  return (
    <AbsoluteFill>
      <SetupHeatsScreenMock />
      <StepLabel step="Step 5" color="#a855f7" title="Divisions & Heats" bullets={["Divisions for organization", "Multi-attempt heats (1-10)", "Overlay style & defaults"]} />
    </AbsoluteFill>
  );
}

function JudgeDashboardScene() {
  return (
    <AbsoluteFill>
      <JudgeScreen />
      <StepLabel step="Live Judging" color={C.accent} title="Judge Interface" bullets={["10 riders visible at once", "0.1 decimal precision", "Live heat notifications", "Admin judge impersonation"]} />
    </AbsoluteFill>
  );
}

function ProducerDashboardScene() {
  return (
    <AbsoluteFill>
      <ProducerScreen />
      <StepLabel step="Live Control" color={C.success} title="Producer Dashboard" bullets={["5 overlay themes", "Per-attempt average scores", "Show/hide each overlay element", "Live OBS preview window"]} />
    </AbsoluteFill>
  );
}

function OBSBroadcastScene() {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill>
      <Img src={IMG.lake} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.5)" }} />

      {/* Title card at start */}
      <AbsoluteFill style={{ opacity: interpolate(frame, [0, 60], [1, 0], { extrapolateRight: "clamp" }) }}>
        <AbsoluteFill style={{ justifyContent: "center", alignItems: "center" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 14, color: C.accent, textTransform: "uppercase", letterSpacing: 3, fontFamily: F.mono, marginBottom: 16 }}>Broadcast View</div>
            <div style={{ fontSize: 52, fontWeight: 800, color: "#fff", marginBottom: 24 }}>What Your Viewers See</div>
            <div style={{ fontSize: 16, color: "#94a3b8" }}>Real-time scores, attempt tracking, and themed overlays — all via OBS Browser Source</div>
          </div>
        </AbsoluteFill>
      </AbsoluteFill>

      {/* Focus + scorecard phase */}
      <AbsoluteFill style={{ opacity: interpolate(frame, [60, 100], [0, 1], { extrapolateRight: "clamp" }) }}>
        <div style={{ position: "absolute", top: 20, left: 30, padding: "3px 10px", background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 8, zIndex: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.danger, boxShadow: `0 0 6px ${C.danger}` }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, fontFamily: F.mono }}>LIVE OBS Output — Steel Theme</span>
        </div>
        <OverlayScreen showFocus={true} showScorecard={true} showLeaderboard={false} />
      </AbsoluteFill>

      {/* Leaderboard phase */}
      <AbsoluteFill style={{ opacity: interpolate(frame, [300, 340], [0, 1], { extrapolateRight: "clamp" }) }}>
        <div style={{ position: "absolute", top: 20, left: 30, padding: "3px 10px", background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", gap: 8, zIndex: 10 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.danger, boxShadow: `0 0 6px ${C.danger}` }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: 1, fontFamily: F.mono }}>LIVE OBS Output — Leaderboard</span>
        </div>
        <OverlayScreen showFocus={false} showScorecard={false} showLeaderboard={true} />
      </AbsoluteFill>
    </AbsoluteFill>
  );
}

function CompetitionHubScene() {
  return (
    <AbsoluteFill>
      <CompetitionViewScreenMock />
      <StepLabel step="Management" color={C.danger} title="Competition Hub" bullets={["Setup, Judge, Producer cards", "Team roster with roles", "OBS overlay URL copy", "Role-based access control"]} />
    </AbsoluteFill>
  );
}

function FeaturesHighlight() {
  const features = [
    { text: "5 Overlay Themes", sub: "Steel · Olympic · Broadcast · Neon · Clean", color: C.accent, img: IMG.wakeFlip },
    { text: "Multi-Attempt Scoring", sub: "Best-of-3 runs with per-attempt averages", color: C.success, img: IMG.wakeJump },
    { text: "Divisions & Heats", sub: "Organize by age, skill, or category", color: C.warning, img: IMG.wakeRide },
    { text: "Real-Time WebSocket", sub: "Scores appear in <100ms everywhere", color: C.danger, img: IMG.lake },
    { text: "Email Invitations", sub: "Judges and producers click a link and join", color: "#a855f7", img: IMG.wakeBoard },
    { text: "Account-Level Riders", sub: "Create once, reuse across all events", color: "#f97316", img: IMG.wakeFlip },
  ];
  return (
    <AbsoluteFill>
      {features.map((f, i) => (
        <Sequence key={f.text} from={i * 105} durationInFrames={105}><FeatureCard {...f} /></Sequence>
      ))}
    </AbsoluteFill>
  );
}

function FeatureCard({ text, sub, color, img }: { text: string; sub: string; color: string; img: string }) {
  const frame = useCurrentFrame();
  const enter = interpolate(frame, [0, 15], [0, 1], { extrapolateRight: "clamp", easing: Easing.out(Easing.cubic) });
  const exit = interpolate(frame, [90, 105], [1, 0], { extrapolateRight: "clamp" });
  const opacity = Math.min(enter, exit);
  const xOff = interpolate(enter, [0, 1], [60, 0]);
  return (
    <AbsoluteFill>
      <Img src={img} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.2)" }} />
      <AbsoluteFill style={{ justifyContent: "center", paddingLeft: 120, opacity }}>
        <div style={{ transform: `translateX(${xOff}px)` }}>
          <div style={{ width: 48, height: 3, background: color, marginBottom: 20 }} />
          <div style={{ fontSize: 52, fontWeight: 800, color: "#fff", lineHeight: 1.1 }}>{text}</div>
          <div style={{ fontSize: 20, color: "#94a3b8", marginTop: 14 }}>{sub}</div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
}

function TrainingScene() {
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center" }}>
      <SlideIn>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 10, color: "#a855f7", textTransform: "uppercase", letterSpacing: 2, fontFamily: F.mono, marginBottom: 14 }}>Onboarding</div>
          <div style={{ fontSize: 44, fontWeight: 800, color: "#fff" }}>Training & Certificates</div>
          <div style={{ fontSize: 16, color: "#94a3b8", marginTop: 14, maxWidth: 560, lineHeight: 1.6 }}>Video courses for judges, producers, and event managers. PDF certificates on completion.</div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 32 }}>
            {["Judge", "Producer", "Event Manager"].map((c) => (
              <div key={c} style={{ padding: "12px 20px", background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: C.text }}>{c} Cert</div>
            ))}
          </div>
        </div>
      </SlideIn>
    </AbsoluteFill>
  );
}

function CTAScene() {
  const frame = useCurrentFrame();
  const opacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill>
      <Img src={IMG.lake} style={{ width: "100%", height: "100%", objectFit: "cover", filter: "brightness(0.15)" }} />
      <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 72, fontWeight: 800, letterSpacing: 8, color: "#fff" }}>RALEY</div>
          <div style={{ fontSize: 20, color: "#94a3b8", marginTop: 12 }}>Professional Competition Scoring</div>
          <div style={{ marginTop: 32, padding: "14px 40px", background: C.accent, color: "#fff", fontSize: 16, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Get Started — Free</div>
          <div style={{ fontSize: 13, color: C.textDim, marginTop: 14, fontFamily: F.mono }}>raley.koda-software.com</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 24 }}>No credit card. No watermarks. Start your first event in minutes.</div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
}

// ─────────────────────────────────────────────────────────────────
// Full-screen mock pages (using exact CSS values from the real app)
// ─────────────────────────────────────────────────────────────────

const shell: React.CSSProperties = { width: "100%", height: "100%", background: C.bg, fontFamily: F.sans, color: C.text, display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 48, background: C.surface, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: F.mono, fontWeight: 800, fontSize: 15, letterSpacing: 2 };
const navLink: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: C.textMuted };
const card: React.CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, padding: 20 };
const inputField: React.CSSProperties = { padding: "8px 12px", background: C.surface, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, fontFamily: F.sans };
const btnPrimary: React.CSSProperties = { padding: "8px 16px", background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${C.accent}` };
const btnSec: React.CSSProperties = { padding: "4px 12px", background: C.surface2, color: C.text, border: `1px solid ${C.border}`, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "inline-flex", alignItems: "center", justifyContent: "center" };

function LoginScreenMock() {
  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <div style={{ marginLeft: "auto", fontSize: 13, fontWeight: 600, color: C.textMuted }}>Sign In</div>
      </div>
      <div style={{ padding: 60, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", flex: 1 }}>
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#fff", marginBottom: 32 }}>Sign in to Raley</div>
          <div style={{ ...inputField, marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 24px" }}>
            <span style={{ fontSize: 16 }}>G</span> <span>Continue with Google</span>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>or sign in with email</div>
          <div style={{ ...inputField, marginBottom: 12 }}><span style={{ color: C.textMuted }}>you@example.com</span></div>
          <div style={{ ...inputField, marginBottom: 24 }}><span style={{ color: C.textMuted }}>••••••••</span></div>
          <div style={{ ...btnPrimary, width: "100%", padding: "10px 24px" }}>Sign In</div>
        </div>
      </div>
    </div>
  );
}

function SetupInviteScreenMock() {
  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <nav style={{ display: "flex", gap: 16 }}>
          <span style={{ ...navLink, color: C.text }}>Competitions</span>
          <span style={navLink}>Riders</span>
        </nav>
        <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 11, color: C.textMuted }}>admin@example.com</span>
      </div>
      <div style={{ padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1, margin: 0 }}>Boardstock Invitational</h1>
            <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>Setup — invite your team</p>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 10 }}>Invite Team</div>
            <div style={card}>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>Email</div>
              <div style={{ ...inputField, marginBottom: 12 }}><span style={{ color: C.textMuted }}>sarah@example.com</span></div>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>Roles</div>
              <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                {["Judge", "Producer", "Admin"].map((role, i) => (
                  <div key={role} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <div style={{ width: 14, height: 14, border: `1px solid ${i === 0 ? C.accent : C.border}`, background: i === 0 ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {i === 0 && <span style={{ color: "#fff", fontSize: 9, fontWeight: 700 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 12, color: C.text }}>{role}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...btnPrimary, fontSize: 11, padding: "6px 14px" }}>Invite</div>
            </div>
            <div style={{ background: "rgba(34,197,94,0.1)", border: `1px solid ${C.success}`, padding: 12, marginTop: 12 }}>
              <div style={{ fontSize: 12, color: C.success, fontWeight: 600 }}>✓ Invite sent to sarah@example.com</div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 10 }}>Team Members</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {[
                { name: "You (Admin)", email: "admin@example.com", roles: ["Owner"] },
                { name: "Mike Chen", email: "mike@example.com", roles: ["Judge", "Producer"] },
                { name: "Sarah Li", email: "sarah@example.com", roles: ["Judge"] },
              ].map((m) => (
                <div key={m.email} style={{ ...card, padding: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: C.textDim }}>{m.email}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {m.roles.map((r) => (
                      <span key={r} style={{ fontSize: 9, padding: "1px 6px", fontWeight: 600, textTransform: "uppercase" as const, background: r === "Owner" ? "rgba(59,130,246,0.15)" : r === "Judge" ? "rgba(234,179,8,0.15)" : "rgba(34,197,94,0.15)", color: r === "Owner" ? C.accent : r === "Judge" ? C.warning : C.success }}>{r}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RidersScreenMock() {
  const riders = [
    { name: "Harley Clifford", nickname: "The King", number: "1", color: "#ef4444", initials: "HC" },
    { name: "Nic Rapa", nickname: "Raptor", number: "7", color: "#3b82f6", initials: "NR" },
    { name: "Dallas Friday", nickname: "D-Fri", number: "12", color: "#22c55e", initials: "DF" },
    { name: "Amber Wing", nickname: "Wings", number: "3", color: "#eab308", initials: "AW" },
  ];
  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <nav style={{ display: "flex", gap: 16 }}>
          <span style={navLink}>Competitions</span>
          <span style={{ ...navLink, color: C.text }}>Riders</span>
        </nav>
        <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 11, color: C.textMuted }}>admin@example.com</span>
      </div>
      <div style={{ padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1 }}>Riders</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>Your rider roster — reusable across all competitions</div>
          </div>
          <div style={btnPrimary}>Add Rider</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
          {riders.map((r) => (
            <div key={r.name} style={{ ...card, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ width: "100%", height: 100, background: C.surface2, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: r.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "#fff" }}>{r.initials}</div>
              </div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{r.name}</div>
              <div style={{ fontSize: 11, color: C.textMuted, fontFamily: F.mono }}>"{r.nickname}" #{r.number}</div>
              <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                <span style={btnSec}>Edit</span>
                <span style={btnSec}>Photo</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SetupHeatsScreenMock() {
  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <nav style={{ display: "flex", gap: 16 }}>
          <span style={{ ...navLink, color: C.text }}>Competitions</span>
          <span style={navLink}>Riders</span>
        </nav>
        <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 11, color: C.textMuted }}>admin@example.com</span>
      </div>
      <div style={{ padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1, margin: 0 }}>Boardstock Invitational</h1>
            <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>Setup — divisions, heats, and settings</p>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 10 }}>Divisions & Heats</div>
            {[
              { name: "Men's Pro", heats: ["Round 1", "Semi-Finals", "Finals"] },
              { name: "Women's Open", heats: ["Heat 1", "Heat 2"] },
            ].map((div) => (
              <div key={div.name} style={{ ...card, padding: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{div.name}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                  {div.heats.map((h) => (
                    <div key={h} style={{ padding: "4px 10px", background: C.surface2, border: `1px solid ${C.border}`, fontSize: 11, color: "#94a3b8" }}>
                      {h} <span style={{ fontFamily: F.mono, fontSize: 9, color: C.textDim, marginLeft: 4 }}>3 att</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ ...btnPrimary, fontSize: 11, padding: "6px 14px", marginTop: 4 }}>New Heat</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 10 }}>Settings</div>
            <div style={{ ...card, display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              {[["Scoring Model", "Average"], ["Judges", "5 judges"], ["Overlay Style", "Steel"]].map(([label, val]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ color: "#94a3b8", fontSize: 12 }}>{label}</span>
                  <span style={{ color: label === "Overlay Style" ? C.accent : "#fff", fontSize: 12, fontWeight: 600 }}>{val}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 8 }}>Overlay Defaults</div>
            <div style={{ ...card, padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
              {["Show Heat Name", "Show Heat Riders", "Show Rider Scores", "Show Leaderboard"].map((opt, i) => (
                <div key={opt} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ width: 14, height: 14, border: `1px solid ${i < 2 ? C.accent : C.border}`, background: i < 2 ? C.accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {i < 2 && <span style={{ color: "#fff", fontSize: 8, fontWeight: 700 }}>✓</span>}
                  </div>
                  <span style={{ fontSize: 12, color: C.text }}>{opt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompetitionViewScreenMock() {
  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <nav style={{ display: "flex", gap: 16 }}>
          <span style={{ ...navLink, color: C.text }}>Competitions</span>
          <span style={navLink}>Riders</span>
        </nav>
        <span style={{ marginLeft: "auto", fontFamily: F.mono, fontSize: 11, color: C.textMuted }}>admin@example.com</span>
      </div>
      <div style={{ padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <h1 style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase" as const, letterSpacing: 1, margin: 0 }}>Boardstock Invitational 2026</h1>
              <span style={{ padding: "2px 8px", fontSize: 10, fontWeight: 700, background: C.danger, color: "#fff", textTransform: "uppercase", letterSpacing: 0.5, fontFamily: F.mono }}>LIVE</span>
            </div>
            <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>Average scoring · 5 judges</p>
            <div style={{ display: "flex", gap: 4, marginTop: 8 }}>
              {["Owner", "Admin", "Producer"].map((r) => (
                <span key={r} style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase" as const, padding: "1px 6px", background: r === "Owner" ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.15)", color: r === "Owner" ? C.accent : C.success }}>{r}</span>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 28 }}>
          {[
            { title: "Setup", desc: "Configure divisions, heats, riders, and invites", color: C.accent },
            { title: "Judge", desc: "Score riders in real-time with multi-attempt support", color: C.warning },
            { title: "Producer", desc: "Control the live OBS overlay with 5 themes", color: C.success },
          ].map((c) => (
            <div key={c.title} style={{ ...card, borderLeft: `3px solid ${c.color}` }}>
              <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase" as const, marginBottom: 8 }}>{c.title}</div>
              <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>{c.desc}</div>
              <div style={{ marginTop: 12, padding: "5px 12px", background: c.color, color: "#fff", fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const, display: "inline-block" }}>Open</div>
            </div>
          ))}
        </div>

        <div style={{ ...card, padding: 12, marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase" as const, marginBottom: 4, fontFamily: F.mono }}>OBS Overlay URL</div>
            <div style={{ fontFamily: F.mono, fontSize: 11, color: C.textDim }}>dev-raley.koda-software.com/obs/abc123...</div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <span style={btnSec}>Copy</span>
            <span style={btnSec}>Open</span>
          </div>
        </div>

        <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase" as const, letterSpacing: "0.05em", color: C.textMuted, marginBottom: 8, fontFamily: F.mono }}>Team</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[
            { name: "You", email: "admin@example.com", judge: "-", roles: ["Owner"] },
            { name: "Mike Chen", email: "mike@example.com", judge: "J1", roles: ["Judge", "Producer"] },
            { name: "Sarah Li", email: "sarah@example.com", judge: "J2", roles: ["Judge"] },
          ].map((m) => (
            <div key={m.email} style={{ display: "grid", gridTemplateColumns: "120px 1fr 40px 80px", gap: 8, padding: "6px 10px", background: C.surface, border: `1px solid ${C.border}`, alignItems: "center", fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>{m.name}</span>
              <span style={{ color: C.textDim }}>{m.email}</span>
              <span style={{ fontFamily: F.mono, color: m.judge !== "-" ? C.warning : C.textDim, textAlign: "center", fontWeight: 600 }}>{m.judge}</span>
              <div style={{ display: "flex", gap: 3, justifyContent: "flex-end" }}>
                {m.roles.map((r) => (
                  <span key={r} style={{ fontSize: 8, padding: "1px 4px", fontWeight: 600, textTransform: "uppercase" as const, background: r === "Owner" ? "rgba(59,130,246,0.15)" : r === "Judge" ? "rgba(234,179,8,0.15)" : "rgba(34,197,94,0.15)", color: r === "Owner" ? C.accent : r === "Judge" ? C.warning : C.success }}>{r}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
